<template>
  <div v-if="visible" class="modal-overlay" @click.self="close">
    <div class="invite-modal">
      <div class="auth-header">
        <h3>{{ roomId ? '邀请好友入局' : '邀请用户' }}</h3>
        <button class="close-btn" type="button" @click="close">×</button>
      </div>
      <div class="invite-body">
        <p class="invite-tip">{{ inviteTip }}</p>
        <p v-if="roomId" class="invite-room">房间号 {{ roomId }}</p>
        <div class="invite-qr-wrap">
          <img
            v-if="qrDataUrl"
            class="invite-qr"
            :src="qrDataUrl"
            alt="邀请二维码"
          >
          <div v-else-if="qrGenerating" class="invite-empty">二维码生成中...</div>
          <div v-else class="invite-empty">{{ inviteLink ? '二维码生成失败' : '暂无邀请链接' }}</div>
        </div>
        <div class="invite-link-row">
          <input
            class="invite-link-input"
            type="text"
            readonly
            :value="inviteLink"
            title="点击复制邀请链接"
            @focus="$event.target.select()"
            @click="copyTextLink"
          >
        </div>
        <div class="invite-actions">
          <button class="invite-btn primary" type="button" :disabled="!inviteLink" @click="copyQrImage">
            {{ copied === 'image' ? '已复制图片' : '复制二维码' }}
          </button>
          <button class="invite-btn" type="button" :disabled="!inviteLink" @click="saveQrImage">
            保存图片
          </button>
          <button
            v-if="showShareButton"
            class="invite-btn"
            type="button"
            :disabled="!inviteLink"
            @click="shareLink"
          >
            分享
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode'

export default {
  name: 'InviteModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    userId: {
      type: [String, Number],
      default: ''
    },
    userName: {
      type: String,
      default: ''
    },
    roomId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      copied: '',
      copyTimer: null,
      isMobile: false,
      qrDataUrl: '',
      qrGenerating: false
    }
  },
  computed: {
    inviteLink() {
      if (!this.userId) return ''
      const base = `${window.location.origin}${window.location.pathname || '/'}`
      const params = new URLSearchParams()
      params.set('invite', String(this.userId))
      if (this.roomId) params.set('room', String(this.roomId))
      return `${base}#/?${params.toString()}`
    },
    inviteTip() {
      return '微信请点「复制二维码」后粘贴发图；若没有图片，请点「保存图片」再发送'
    },
    inviteCopyText() {
      const name = this.userName ? `${this.userName} ` : ''
      if (this.roomId) {
        return `${name}邀请你加入十三水房间 ${this.roomId}\n${this.inviteLink}`
      }
      return `${name}邀请你一起来玩十三水\n${this.inviteLink}`
    },
    canNativeShare() {
      return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
    },
    showShareButton() {
      return this.isMobile && this.canNativeShare
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.detectMobile()
        this.copied = ''
        this.generateQrCode()
      }
    },
    inviteLink() {
      if (this.visible) this.generateQrCode()
    }
  },
  mounted() {
    this.detectMobile()
    if (this.visible) this.generateQrCode()
  },
  beforeDestroy() {
    if (this.copyTimer) {
      clearTimeout(this.copyTimer)
      this.copyTimer = null
    }
  },
  methods: {
    detectMobile() {
      this.isMobile = window.innerWidth <= 768 ||
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    },

    async generateQrCode() {
      if (!this.inviteLink) {
        this.qrDataUrl = ''
        this.qrGenerating = false
        return
      }
      this.qrGenerating = true
      try {
        this.qrDataUrl = await QRCode.toDataURL(this.inviteLink, {
          width: 180,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        })
      } catch (e) {
        this.qrDataUrl = ''
        console.warn('生成邀请二维码失败:', e)
      } finally {
        this.qrGenerating = false
      }
    },

    close() {
      this.$emit('close')
    },

    dataUrlToBlob(dataUrl) {
      const parts = String(dataUrl || '').split(',')
      if (parts.length < 2) return null
      const mime = (parts[0].match(/:(.*?);/) || [])[1] || 'image/png'
      const binary = atob(parts[1])
      const bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
      return new Blob([bytes], { type: mime })
    },

    blobToDataUrl(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(new Error('读取邀请图失败'))
        reader.readAsDataURL(blob)
      })
    },

    async buildInviteCardBlob() {
      if (!this.inviteLink) return null
      const width = 360
      const padding = 28
      const qrSize = 240
      const title = this.roomId ? '邀请好友入局' : '邀请用户'
      const subtitle = this.roomId
        ? `房间号 ${this.roomId}`
        : (this.userName ? `${this.userName} 邀请你一起来玩十三水` : '扫描二维码即可加入')
      const footer = '扫描二维码即可加入'
      const height = padding + 36 + 28 + qrSize + 36 + padding

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) return null

      ctx.fillStyle = '#16213e'
      ctx.fillRect(0, 0, width, height)

      ctx.fillStyle = '#ffd700'
      ctx.font = 'bold 22px sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      ctx.fillText(title, width / 2, padding)

      ctx.fillStyle = '#ffffff'
      ctx.font = '16px sans-serif'
      ctx.fillText(subtitle, width / 2, padding + 36)

      const qrX = (width - qrSize) / 2
      const qrY = padding + 36 + 28
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20)

      const qrCanvas = document.createElement('canvas')
      await QRCode.toCanvas(qrCanvas, this.inviteLink, {
        width: qrSize,
        margin: 1,
        color: { dark: '#000000', light: '#ffffff' }
      })
      ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize)

      ctx.fillStyle = 'rgba(255, 255, 255, 0.72)'
      ctx.font = '13px sans-serif'
      ctx.fillText(footer, width / 2, qrY + qrSize + 18)

      return await new Promise((resolve, reject) => {
        canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('生成邀请图失败'))), 'image/png')
      })
    },

    markCopied(kind) {
      this.copied = kind
      if (this.copyTimer) clearTimeout(this.copyTimer)
      this.copyTimer = setTimeout(() => {
        this.copied = ''
        this.copyTimer = null
      }, 2000)
    },

    async getInviteImageBlob() {
      try {
        const card = await this.buildInviteCardBlob()
        if (card) return card
      } catch (e) {
        console.warn('生成邀请图失败:', e)
      }
      if (this.qrDataUrl) return this.dataUrlToBlob(this.qrDataUrl)
      return null
    },

    async writeImageBlob(blob) {
      if (!navigator.clipboard || typeof window.ClipboardItem !== 'function') {
        throw new Error('当前环境不支持复制图片')
      }
      const type = blob.type || 'image/png'
      // 微信会优先取纯文本，这里只写入图片，不能附带 text/plain
      try {
        await navigator.clipboard.write([new ClipboardItem({ [type]: blob })])
        return
      } catch (e) {
        await navigator.clipboard.write([new ClipboardItem({ [type]: Promise.resolve(blob) })])
      }
    },

    async copyImageByExecCommand(blob) {
      const dataUrl = await this.blobToDataUrl(blob)
      const wrap = document.createElement('div')
      wrap.contentEditable = 'true'
      wrap.setAttribute('contenteditable', 'true')
      wrap.style.cssText = 'position:fixed;left:0;top:0;width:1px;height:1px;opacity:0.01;z-index:2147483647;overflow:hidden;'
      const img = document.createElement('img')
      img.src = dataUrl
      img.alt = '邀请二维码'
      wrap.appendChild(img)
      document.body.appendChild(wrap)

      try {
        await new Promise((resolve, reject) => {
          if (img.complete) {
            resolve()
            return
          }
          img.onload = () => resolve()
          img.onerror = () => reject(new Error('邀请图加载失败'))
        })
        wrap.focus()
        const range = document.createRange()
        range.selectNode(img)
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(range)
        const ok = document.execCommand('copy')
        sel.removeAllRanges()
        if (!ok) throw new Error('复制失败')
      } finally {
        document.body.removeChild(wrap)
      }
    },

    async copyTextFallback(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text)
        return
      }
      const input = this.$el.querySelector('.invite-link-input')
      if (!input) throw new Error('无法复制')
      input.focus()
      input.select()
      const ok = document.execCommand('copy')
      if (!ok) throw new Error('复制失败')
    },

    async copyQrImage() {
      if (!this.inviteLink) return
      try {
        const imageBlob = await this.getInviteImageBlob()
        if (!imageBlob) throw new Error('二维码尚未生成')
        try {
          await this.writeImageBlob(imageBlob)
        } catch (e) {
          await this.copyImageByExecCommand(imageBlob)
        }
        this.markCopied('image')
        this.$message.success('二维码已复制，打开微信粘贴即可发图')
      } catch (e) {
        try {
          await this.saveQrImage(true)
          this.$message.warning('当前浏览器无法直接复制图片，已为你保存二维码，请发到微信')
        } catch (saveErr) {
          this.$message.error(e.message || '复制失败，请点「保存图片」后发到微信')
        }
      }
    },

    async copyTextLink() {
      if (!this.inviteLink) return
      try {
        await this.copyTextFallback(this.inviteCopyText)
        this.markCopied('text')
        this.$message.success('邀请链接已复制')
      } catch (e) {
        this.$message.error(e.message || '复制失败，请手动复制')
      }
    },

    async saveQrImage(silent) {
      if (!this.inviteLink) return
      const hideToast = silent === true
      const imageBlob = await this.getInviteImageBlob()
      if (!imageBlob) throw new Error('二维码尚未生成')
      const fileName = this.roomId ? `邀请房间${this.roomId}.png` : '邀请二维码.png'
      const url = URL.createObjectURL(imageBlob)
      try {
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        a.rel = 'noopener'
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        if (!hideToast) this.$message.success('二维码已保存，可从相册发到微信')
      } finally {
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      }
    },

    async shareLink() {
      if (!this.inviteLink || !this.canNativeShare) return
      const title = this.roomId ? `邀请加入房间 ${this.roomId}` : '邀请加入十三水'
      const text = this.inviteCopyText
      try {
        const imageBlob = await this.getInviteImageBlob()
        if (imageBlob) {
          const file = new File([imageBlob], this.roomId ? `邀请房间${this.roomId}.png` : '邀请二维码.png', {
            type: imageBlob.type || 'image/png'
          })
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ title, text, files: [file] })
            return
          }
        }
        await navigator.share({ title, text, url: this.inviteLink })
      } catch (e) {
        if (e && e.name === 'AbortError') return
        this.$message.error(e.message || '分享失败')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
}

.invite-modal {
  background: linear-gradient(135deg, #1a1a2e, #16213e);
  border-radius: 16px;
  padding: 24px;
  width: min(100%, 420px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.auth-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  h3 {
    color: #ffd700;
    margin: 0;
  }

  .close-btn {
    width: 32px;
    height: 32px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.8);
    font-size: 20px;
    cursor: pointer;
  }
}

.invite-body {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 14px;
}

.invite-tip {
  margin: 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
  line-height: 1.5;
}

.invite-room {
  margin: -6px 0 0;
  text-align: center;
  color: #ffd700;
  font-size: 15px;
  font-weight: bold;
  letter-spacing: 1px;
}

.invite-qr-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.96);
}

.invite-qr {
  width: 180px;
  height: 180px;
  display: block;
  -webkit-touch-callout: default;
  -webkit-user-select: none;
  user-select: none;
}

.invite-empty {
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
}

.invite-link-row {
  width: 100%;
}

.invite-link-input {
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 0 12px;
  border: 1px solid rgba(255, 215, 0, 0.28);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.28);
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
  outline: none;
  cursor: pointer;
}

.invite-actions {
  display: flex;
  gap: 10px;
}

.invite-btn {
  flex: 1;
  height: 40px;
  border: 1px solid rgba(255, 215, 0, 0.36);
  border-radius: 8px;
  background: rgba(255, 215, 0, 0.1);
  color: #ffd700;
  font-size: 14px;
  cursor: pointer;

  &.primary {
    background: linear-gradient(180deg, rgba(255, 215, 0, 0.28), rgba(255, 140, 0, 0.16));
    border-color: rgba(255, 215, 0, 0.55);
    color: #fff7d5;
    font-weight: 600;
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }

  &:hover:not(:disabled) {
    border-color: rgba(255, 215, 0, 0.72);
    background: rgba(255, 215, 0, 0.18);
  }

  &.primary:hover:not(:disabled) {
    background: linear-gradient(180deg, rgba(255, 215, 0, 0.36), rgba(255, 140, 0, 0.22));
  }
}

@media (max-width: 768px) {
  .invite-modal {
    width: min(100%, 360px);
    padding: 18px;
  }

  .invite-qr,
  .invite-qr-wrap {
    min-height: 150px;
  }

  .invite-qr {
    width: 150px;
    height: 150px;
  }
}
</style>
